<template>
	<v-dialog v-model="dialogOpen" max-width="1080" persistent scrollable>
		<v-card class="viagem-dialog" outlined>
			<v-card-title class="viagem-dialog__title">
				<div>
					<div>{{ dialogTitle }}</div>
					<div class="despesa-dialog__subtitle">Lancamentos vinculados diretamente a esta viagem.</div>
				</div>
				<v-spacer />
				<v-btn icon :disabled="saving || deleting" @click="fecharDialog">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-divider />

			<v-card-text class="pt-6 pb-4">
				<div class="despesa-resumo">
					<div class="despesa-resumo__card">
						<div class="despesa-resumo__label">Total em despesas</div>
						<div class="despesa-resumo__value">{{ formatCurrency(totalDespesas) }}</div>
					</div>
					<div class="despesa-resumo__card">
						<div class="despesa-resumo__label">Lancamentos</div>
						<div class="despesa-resumo__value">{{ despesas.length }}</div>
					</div>
					<v-spacer />
					<v-btn class="viagem-new-btn" depressed @click="abrirNovaDespesa">
						<v-icon left size="18">mdi-plus</v-icon>
						Nova despesa
					</v-btn>
				</div>

				<v-expand-transition>
					<v-card v-if="formOpen" class="despesa-form-card mt-5" outlined>
						<v-card-title class="despesa-form-card__title">
							<span>{{ formTitle }}</span>
							<v-spacer />
							<v-btn text small color="#10b981" :disabled="saving" @click="cancelarFormulario">
								Cancelar
							</v-btn>
						</v-card-title>
						<v-divider />
						<v-card-text class="pt-5 pb-2">
							<v-form ref="despesaForm" v-model="formValid" lazy-validation>
								<v-row dense>
									<v-col cols="12" md="4">
										<v-select
											v-model="form.tipo"
											:items="tipoOptions"
											label="Tipo de despesa"
											item-text="label"
											item-value="value"
											outlined
											dense
											:rules="[rules.required]"
										/>
									</v-col>

									<v-col cols="12" md="4">
										<v-text-field
											v-model.trim="form.descricao"
											label="Descricao"
											outlined
											dense
										/>
									</v-col>

									<v-col cols="12" md="2">
										<v-text-field
											v-model="form.valor"
											label="Valor"
											outlined
											dense
											prefix="R$"
											:rules="[rules.required, rules.currency]"
											@input="onValorInput"
										/>
									</v-col>

									<v-col cols="12" md="2">
										<v-text-field
											v-model="form.data_despesa"
											label="Data"
											type="date"
											outlined
											dense
											:rules="[rules.required]"
										/>
									</v-col>

									<v-col v-for="field in selectedExtraFields" :key="field.key" cols="12" md="4">
											<v-select
												v-if="field.type === 'select'"
												:value="getExtraFieldValue(field.key)"
												:items="field.items || []"
												:label="field.label"
												outlined
												dense
												@input="onExtraFieldInput(field, $event)"
											/>

											<v-text-field
												v-else
												:value="getExtraFieldValue(field.key)"
												:label="field.label"
												:prefix="field.type === 'currency' ? 'R$' : undefined"
												:type="field.type === 'date' ? 'date' : 'text'"
												:inputmode="field.type === 'number' || field.type === 'currency' ? 'decimal' : undefined"
												outlined
												dense
												@input="onExtraFieldInput(field, $event)"
											/>
									</v-col>

									<v-col cols="12">
										<v-textarea
											v-model.trim="form.observacoes"
											label="Observacoes"
											outlined
											dense
											rows="2"
											auto-grow
										/>
									</v-col>
								</v-row>
							</v-form>
						</v-card-text>
						<v-card-actions class="px-5 pb-5 pt-0">
							<v-spacer />
							<v-btn text :disabled="saving" @click="cancelarFormulario">Cancelar</v-btn>
							<v-btn class="viagem-new-btn" depressed :loading="saving" :disabled="saving" @click="salvarDespesa">
								{{ formSaveLabel }}
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-expand-transition>

				<v-data-table
					class="viagem-table mt-6"
					:headers="headers"
					:items="despesas"
					:loading="loading"
					loading-text="Carregando despesas..."
					no-data-text="Nenhuma despesa cadastrada para esta viagem"
					:disable-pagination="despesas.length <= 8"
					:hide-default-footer="despesas.length <= 8"
				>
					<template v-slot:[`item.tipo`]='{ item }'>
						<span class="despesa-tipo-badge" :class="getTipoChipClass(item.tipo)">
							<v-icon size="14" class="mr-1">{{ getTipoIcon(item.tipo) }}</v-icon>
							{{ getTipoLabel(item.tipo) }}
							<template v-if="isObservacaoWhatsapp(item.observacoes)">
								<v-icon size="16" color="#ef4444" class="ml-2">mdi-comment-account-outline</v-icon>
							</template>
						</span>
					</template>

					<template v-slot:[`item.data_despesa`]='{ item }'>
						<span>{{ formatDate(item.data_despesa) }}</span>
					</template>

					<template v-slot:[`item.valor`]='{ item }'>
						<strong>{{ formatCurrency(item.valor) }}</strong>
					</template>

					<template v-slot:[`item.odometro`]='{ item }'>
						<div class="despesa-odometro">
							<v-icon size="15" class="mr-1">mdi-counter</v-icon>
							<span>{{ formatOdometro(item.odometro) }}</span>
						</div>
					</template>

					<template v-slot:[`item.detalhes`]='{ item }'>
						<div class="despesa-detalhes">{{ resumirExtras(item) }}</div>
					</template>

					<template v-slot:[`item.comprovantes`]='{ item }'>
						<div class="despesa-comprovantes">
							<template v-if="getMidiasDespesa(item).length">
								<v-tooltip
									v-for="midia in getMidiasDespesa(item)"
									:key="midia.tipo"
									bottom
								>
									<template v-slot:activator="{ on, attrs }">
										<v-btn
											icon
											small
											class="despesa-comprovante-acao"
											:class="midia.botaoClass"
											v-bind="attrs"
											v-on="on"
											@click="abrirPreviewUrl(midia)"
										>
											<v-icon size="18">{{ midia.icon }}</v-icon>
										</v-btn>
									</template>
									<span>{{ midia.tooltip }}</span>
								</v-tooltip>
							</template>
							<span v-else class="despesa-comprovantes__vazio">Sem anexos</span>
						</div>
					</template>

					<template v-slot:[`item.acoes`]='{ item }'>
						<div class="despesa-acoes">
							<v-btn icon small @click="editarDespesa(item)">
								<v-icon color="#10b981" size="18">mdi-pencil-outline</v-icon>
							</v-btn>
							<v-btn icon small @click="abrirDialogExcluir(item)">
								<v-icon color="#ef4444" size="18">mdi-delete-outline</v-icon>
							</v-btn>
						</div>
					</template>
				</v-data-table>
			</v-card-text>

			<v-card-actions class="px-6 pb-5 pt-0">
				<v-spacer />
				<v-btn text :disabled="saving || deleting" @click="fecharDialog">Fechar</v-btn>
			</v-card-actions>
		</v-card>

		<v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
			<v-card class="viagem-dialog" outlined>
				<v-card-title class="viagem-dialog__title">
					<span>Excluir despesa</span>
					<v-spacer />
					<v-btn icon :disabled="deleting" @click="deleteDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<div class="viagem-confirmacao__texto">
						Tem certeza que deseja excluir a despesa <strong>{{ despesaExclusao.descricao || '-' }}</strong>?
					</div>
					<div class="viagem-confirmacao__aviso">
						Essa exclusao atualiza o total da viagem imediatamente.
					</div>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="deleting" @click="deleteDialogOpen = false">Cancelar</v-btn>
					<v-btn class="viagem-delete-btn" depressed :loading="deleting" :disabled="deleting" @click="confirmarExclusaoDespesa">
						Excluir
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="previewDialogOpen" max-width="920">
			<v-card class="viagem-dialog" outlined>
				<v-card-title class="viagem-dialog__title">
					<span>{{ previewMidia.titulo || 'Visualizar comprovante' }}</span>
					<v-spacer />
					<v-btn
						v-if="previewMidia.url"
						icon
						@click="abrirMidiaEmNovaAba(previewMidia.url)"
					>
						<v-icon>mdi-open-in-new</v-icon>
					</v-btn>
					<v-btn icon @click="fecharPreviewMidia">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pa-6">
					<div v-if="previewMidia.url" class="despesa-preview-wrap">
						<img :src="previewMidia.url" :alt="previewMidia.titulo" class="despesa-preview-imagem" />
					</div>
					<div v-else class="despesa-preview-vazio">Arquivo indisponível.</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-dialog>
</template>

<script>
import viagensServices from './viagensServices';
import {
	formatCurrencyFieldValue,
	formatCurrencyInputValue,
} from '../financeiro/financeiroCurrency';
import { VIAGEM_DESPESA_TIPOS, getViagemDespesaTipoMeta } from './viagemDespesaTipos';

function getToday() {
	return new Date().toISOString().slice(0, 10);
}

function createInitialForm() {
	return {
		id: null,
		tipo: null,
		descricao: '',
		valor: '',
		data_despesa: getToday(),
		observacoes: '',
		dados_extras: {},
	};
}

const TIPO_VISUAL_MAP = {
	combustivel: { icon: 'mdi-fuel', className: 'despesa-tipo-badge--fuel' },
	arla: { icon: 'mdi-fuel', className: 'despesa-tipo-badge--fuel' },
	pedagio: { icon: 'mdi-map-marker-path', className: 'despesa-tipo-badge--route' },
	alimentacao: { icon: 'mdi-silverware-fork-knife', className: 'despesa-tipo-badge--support' },
	hospedagem: { icon: 'mdi-bed-outline', className: 'despesa-tipo-badge--support' },
	estacionamento_patio: { icon: 'mdi-parking', className: 'despesa-tipo-badge--route' },
	carga_descarga_chapa: { icon: 'mdi-package-variant-closed', className: 'despesa-tipo-badge--operation' },
	manutencao: { icon: 'mdi-wrench-outline', className: 'despesa-tipo-badge--vehicle' },
	pneus_borracharia: { icon: 'mdi-wrench-outline', className: 'despesa-tipo-badge--vehicle' },
	lavagem: { icon: 'mdi-water-outline', className: 'despesa-tipo-badge--vehicle' },
	balanca_pesagem: { icon: 'mdi-weight-kilogram', className: 'despesa-tipo-badge--vehicle' },
	travessia_balsa_ferry: { icon: 'mdi-map-marker-path', className: 'despesa-tipo-badge--route' },
	diaria_adiantamento: { icon: 'mdi-wallet-outline', className: 'despesa-tipo-badge--support' },
	comissao_motorista: { icon: 'mdi-cash-multiple', className: 'despesa-tipo-badge--support' },
	escolta_apoio_operacional: { icon: 'mdi-shield-outline', className: 'despesa-tipo-badge--operation' },
	armazenagem_transbordo: { icon: 'mdi-warehouse', className: 'despesa-tipo-badge--operation' },
	multa_ocorrencia: { icon: 'mdi-alert-circle-outline', className: 'despesa-tipo-badge--alert' },
	outros: { icon: 'mdi-tag-outline', className: 'despesa-tipo-badge--neutral' },
};

const DEFAULT_TIPO_VISUAL = {
	icon: 'mdi-tag-outline',
	className: 'despesa-tipo-badge--neutral',
};

export default {
	name: 'ViagemDespesasDialog',

	props: {
		value: {
			type: Boolean,
			default: false,
		},
		viagem: {
			type: Object,
			default: null,
		},
	},

	data() {
		return {
			dialogOpen: false,
			loading: false,
			saving: false,
			deleting: false,
			formOpen: false,
			formValid: false,
			deleteDialogOpen: false,
			previewDialogOpen: false,
			despesaExclusao: {},
			previewMidia: {
				titulo: '',
				url: '',
			},
			despesas: [],
			form: createInitialForm(),
			headers: [
				{ text: 'Tipo', value: 'tipo', sortable: false },
				{ text: 'Descricao', value: 'descricao', sortable: false },
				{ text: 'Data', value: 'data_despesa', sortable: false },
				{ text: 'Valor', value: 'valor', sortable: false },
				{ text: 'Odometro', value: 'odometro', sortable: false },
				{ text: 'Detalhes', value: 'detalhes', sortable: false },
				{ text: 'Comprovantes', value: 'comprovantes', sortable: false },
				{ text: 'Acoes', value: 'acoes', sortable: false, align: 'center' },
			],
			rules: {
				required: (v) => Boolean(v || v === 0) || 'Campo obrigatorio',
				currency: (v) => {
					if (!v) {
						return true;
					}

					const numericValue = this.parseCurrency(v);
					return Number.isFinite(numericValue) && numericValue >= 0 || 'Informe um valor valido';
				},
			},
		};
	},

	computed: {
		dialogTitle() {
			const codigo = this.viagem?.codigo || this.viagem?.codigo_carga || (this.viagem?.id ? `#${this.viagem.id}` : '-');
			return `Despesas da viagem ${codigo}`;
		},

		formTitle() {
			return this.form.id ? 'Editar despesa' : 'Nova despesa';
		},

		formSaveLabel() {
			return this.form.id ? 'Salvar alteracoes' : 'Salvar despesa';
		},

		tipoOptions() {
			return VIAGEM_DESPESA_TIPOS;
		},

		selectedTipoMeta() {
			return getViagemDespesaTipoMeta(this.form.tipo);
		},

		selectedExtraFields() {
			return this.selectedTipoMeta?.extraFields || [];
		},

		totalDespesas() {
			return this.despesas.reduce((total, item) => {
				const numericValue = Number(item?.valor);
				return Number.isFinite(numericValue) ? total + numericValue : total;
			}, 0);
		},
	},

	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.dialogOpen = newValue;

				if (newValue) {
					this.prepararDialog();
				}
			},
		},

		dialogOpen(newValue) {
			this.$emit('input', newValue);

			if (!newValue) {
				this.resetState();
			}
		},

		'viagem.id'(newValue, oldValue) {
			if (this.dialogOpen && newValue && newValue !== oldValue) {
				this.prepararDialog();
			}
		},

		'form.tipo'(newValue, oldValue) {
			if (newValue === oldValue) {
				return;
			}

			const allowedKeys = new Set((getViagemDespesaTipoMeta(newValue)?.extraFields || []).map((field) => field.key));
			const nextExtras = Object.entries(this.form.dados_extras || {}).reduce((accumulator, [key, value]) => {
				if (allowedKeys.has(key)) {
					accumulator[key] = value;
				}
				return accumulator;
			}, {});

			this.$set(this.form, 'dados_extras', nextExtras);

			if (!this.form.id && !String(this.form.descricao || '').trim()) {
				this.form.descricao = getViagemDespesaTipoMeta(newValue)?.label || '';
			}
		},
	},

	methods: {
		isObservacaoWhatsapp(observacao) {
			if (!observacao) return false;
			return String(observacao).trim().endsWith('Lançamento enviado pelo motorista via WhatsApp.');
		},
		
		async prepararDialog() {
			if (!this.viagem?.id) {
				this.despesas = [];
				return;
			}

			this.cancelarFormulario();
			await this.carregarDespesas();
		},

		resetState() {
			this.loading = false;
			this.saving = false;
			this.deleting = false;
			this.deleteDialogOpen = false;
			this.previewDialogOpen = false;
			this.despesaExclusao = {};
			this.previewMidia = { titulo: '', url: '' };
			this.despesas = [];
			this.cancelarFormulario();
		},

		async carregarDespesas() {
			if (!this.viagem?.id) {
				return;
			}

			this.loading = true;
			try {
				this.despesas = await viagensServices.listarDespesas(this.viagem.id);
			} catch (error) {
				this.despesas = [];
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao carregar despesas da viagem.');
			} finally {
				this.loading = false;
			}
		},

		abrirNovaDespesa() {
			this.form = createInitialForm();
			this.formOpen = true;
			this.formValid = false;
			this.$nextTick(() => {
				this.$refs?.despesaForm?.resetValidation?.();
			});
		},

		cancelarFormulario() {
			this.formOpen = false;
			this.formValid = false;
			this.form = createInitialForm();
			this.$nextTick(() => {
				this.$refs?.despesaForm?.resetValidation?.();
			});
		},

		editarDespesa(item) {
			if (!item?.id) {
				return;
			}

			this.form = {
				id: item.id,
				tipo: item.tipo,
				descricao: item.descricao || '',
				valor: formatCurrencyFieldValue(item.valor),
				data_despesa: item.data_despesa || getToday(),
				observacoes: item.observacoes || '',
				dados_extras: this.hidratarDadosExtras(item.tipo, item.dados_extras, item.odometro),
			};

			this.formOpen = true;
			this.formValid = false;
			this.$nextTick(() => {
				this.$refs?.despesaForm?.resetValidation?.();
			});
		},

		hidratarDadosExtras(tipo, dadosExtras, odometro) {
			const extraFields = getViagemDespesaTipoMeta(tipo)?.extraFields || [];
			const fieldMap = extraFields.reduce((accumulator, field) => {
				accumulator[field.key] = field;
				return accumulator;
			}, {});
			const source = dadosExtras && typeof dadosExtras === 'object' ? dadosExtras : {};
			const payload = {};

			if (fieldMap.quilometragem && odometro !== null && odometro !== undefined && odometro !== '') {
				payload.quilometragem = String(odometro);
			}

			Object.entries(source).forEach(([key, value]) => {
				const field = fieldMap[key];
				if (!field || value === null || value === undefined || value === '') {
					return;
				}

				payload[key] = field.type === 'currency'
					? formatCurrencyFieldValue(value)
					: String(value);
			});

			return payload;
		},

		getExtraFieldValue(key) {
			return this.form?.dados_extras?.[key] ?? '';
		},

		onExtraFieldInput(field, value) {
			if (!this.form.dados_extras || typeof this.form.dados_extras !== 'object') {
				this.$set(this.form, 'dados_extras', {});
			}

			const normalizedValue = field.type === 'currency'
					? formatCurrencyInputValue(value)
				: value;

			if (normalizedValue === null || normalizedValue === undefined || normalizedValue === '') {
				this.$delete(this.form.dados_extras, field.key);
				return;
			}

			this.$set(this.form.dados_extras, field.key, normalizedValue);
		},

		parseCurrency(value) {
			if (value === null || value === undefined || value === '') {
				return null;
			}

			if (typeof value === 'number') {
				return Number.isFinite(value) ? value : null;
			}

			const normalized = String(value)
				.replace(/R\$/gi, '')
				.replace(/\s/g, '')
				.trim();

			if (!normalized) {
				return null;
			}

			const hasComma = normalized.includes(',');
			const sanitized = hasComma
				? normalized.replace(/\./g, '').replace(/,/g, '.')
				: normalized.replace(/,/g, '');

			const parsed = Number(sanitized);
			return Number.isFinite(parsed) ? parsed : null;
		},

		onValorInput(value) {
			this.form.valor = formatCurrencyInputValue(value);
		},

		buildDadosExtrasPayload() {
			const fieldMap = this.selectedExtraFields.reduce((accumulator, field) => {
				accumulator[field.key] = field;
				return accumulator;
			}, {});

			return Object.entries(this.form.dados_extras || {}).reduce((accumulator, [key, value]) => {
				if (key === 'quilometragem') {
					return accumulator;
				}

				const field = fieldMap[key] || null;
				if (value === null || value === undefined || value === '') {
					return accumulator;
				}

				if (field?.type === 'currency') {
					const numericValue = this.parseCurrency(value);
					if (numericValue !== null) {
						accumulator[key] = numericValue;
					}
					return accumulator;
				}

				if (field?.type === 'number') {
					const numericValue = Number(String(value).replace(',', '.'));
					accumulator[key] = Number.isFinite(numericValue) ? numericValue : String(value).trim();
					return accumulator;
				}

				const textValue = typeof value === 'string' ? String(value).trim() : value;
				if (textValue) {
					accumulator[key] = textValue;
				}

				return accumulator;
			}, {});
		},

		async salvarDespesa() {
			const isValid = this.$refs?.despesaForm?.validate?.();
			if (!isValid || !this.viagem?.id) {
				return;
			}

			const odometro = this.form?.dados_extras?.quilometragem
				? Number(String(this.form.dados_extras.quilometragem).replace(/\D/g, ''))
				: null;

			const payload = {
				tipo: this.form.tipo,
				descricao: String(this.form.descricao || '').trim() || null,
				valor: this.parseCurrency(this.form.valor),
				data_despesa: this.form.data_despesa,
				odometro: Number.isFinite(odometro) ? odometro : null,
				observacoes: String(this.form.observacoes || '').trim() || null,
				dados_extras: this.buildDadosExtrasPayload(),
			};

			this.saving = true;
			try {
				const response = this.form.id
					? await viagensServices.atualizarDespesa(this.viagem.id, this.form.id, payload)
					: await viagensServices.criarDespesa(this.viagem.id, payload);

				this.$toasted?.show(this.form.id ? 'Despesa atualizada com sucesso.' : 'Despesa cadastrada com sucesso.');
				await this.carregarDespesas();
				this.cancelarFormulario();
				this.$emit('saved', {
					viagemId: this.viagem.id,
					despesa: response?.data || null,
				});
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao salvar despesa da viagem.');
			} finally {
				this.saving = false;
			}
		},

		abrirDialogExcluir(item) {
			this.despesaExclusao = item || {};
			this.deleteDialogOpen = true;
		},

		async confirmarExclusaoDespesa() {
			if (!this.viagem?.id || !this.despesaExclusao?.id) {
				return;
			}

			this.deleting = true;
			try {
				await viagensServices.excluirDespesa(this.viagem.id, this.despesaExclusao.id);
				this.$toasted?.show('Despesa excluida com sucesso.');
				this.deleteDialogOpen = false;
				if (this.form.id && String(this.form.id) === String(this.despesaExclusao.id)) {
					this.cancelarFormulario();
				}
				this.despesaExclusao = {};
				await this.carregarDespesas();
				this.$emit('saved', { viagemId: this.viagem.id });
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao excluir despesa da viagem.');
			} finally {
				this.deleting = false;
			}
		},

		getTipoLabel(tipo) {
			return getViagemDespesaTipoMeta(tipo)?.label || tipo || '-';
		},

		getTipoVisual(tipo) {
			return TIPO_VISUAL_MAP[tipo] || DEFAULT_TIPO_VISUAL;
		},

		getTipoChipClass(tipo) {
			return this.getTipoVisual(tipo).className;
		},

		getTipoIcon(tipo) {
			return this.getTipoVisual(tipo).icon;
		},

		resumirExtras(item) {
			const meta = getViagemDespesaTipoMeta(item?.tipo);
			const fieldMap = (meta?.extraFields || []).reduce((accumulator, field) => {
				accumulator[field.key] = field;
				return accumulator;
			}, {});
			const values = [];

			if (item?.odometro !== null && item?.odometro !== undefined && fieldMap.quilometragem) {
				values.push(`${fieldMap.quilometragem.label}: ${String(item.odometro)}`);
			}

			Object.entries(item?.dados_extras || {}).forEach(([key, value]) => {
				const field = fieldMap[key];
				if (!field || value === null || value === undefined || value === '') {
					return;
				}

				const displayValue = field.type === 'currency'
					? this.formatCurrency(value)
					: String(value);

				values.push(`${field.label}: ${displayValue}`);
			});

			return values.length ? values.join(' | ') : '-';
		},

		formatCurrency(value) {
			if (value === null || value === undefined || value === '') {
				return '-';
			}

			const numericValue = Number(value);
			if (!Number.isFinite(numericValue)) {
				return String(value);
			}

			return numericValue.toLocaleString('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			});
		},

		formatDate(value) {
			if (!value) {
				return '-';
			}

			const date = new Date(value);
			if (Number.isNaN(date.getTime())) {
				return String(value);
			}

			return date.toLocaleDateString('pt-BR');
		},

		formatOdometro(value) {
			if (value === null || value === undefined || value === '') {
				return '-';
			}
			return String(value);
		},

		getUploadsBaseUrl() {
			const baseUrl = String(process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '');
			return baseUrl.replace(/\/api$/, '');
		},

		montarUrlUpload(caminhoRelativo) {
			if (!caminhoRelativo) {
				return '';
			}

			const baseUrl = this.getUploadsBaseUrl();
			const caminho = String(caminhoRelativo).replace(/^\/+/, '');
			return `${baseUrl}/uploads/${caminho}`;
		},

		getMidiasDespesa(item) {
			const midias = [];

			if (item?.foto_odometro_caminho) {
				midias.push({
					tipo: 'odometro',
					titulo: 'Odômetro',
					icon: 'mdi-camera-outline',
					tooltip: 'Visualizar foto do odômetro',
					botaoClass: 'despesa-comprovante-acao--odometro',
					url: this.montarUrlUpload(item.foto_odometro_caminho),
				});
			}

			if (item?.foto_comprovante_caminho) {
				midias.push({
					tipo: 'comprovante',
					titulo: 'Comprovante',
					icon: 'mdi-receipt-text-outline',
					tooltip: 'Visualizar comprovante',
					botaoClass: 'despesa-comprovante-acao--comprovante',
					url: this.montarUrlUpload(item.foto_comprovante_caminho),
				});
			}

			return midias.filter((midia) => Boolean(midia.url));
		},

		abrirPreviewMidia(item, tipo) {
			const isOdometro = tipo === 'odometro';
			const caminho = isOdometro ? item?.foto_odometro_caminho : item?.foto_comprovante_caminho;
			const url = this.montarUrlUpload(caminho);

			if (!url) {
				this.$toasted?.show('Arquivo não encontrado.');
				return;
			}

			this.previewMidia = {
				titulo: isOdometro ? 'Foto do odômetro' : 'Foto do comprovante',
				url,
			};
			this.previewDialogOpen = true;
		},

		abrirPreviewUrl(midia) {
			if (!midia?.url) {
				this.$toasted?.show('Arquivo não encontrado.');
				return;
			}

			this.previewMidia = {
				titulo: midia.titulo,
				url: midia.url,
			};
			this.previewDialogOpen = true;
		},

		abrirMidiaEmNovaAba(url) {
			if (!url) {
				return;
			}

			window.open(url, '_blank', 'noopener');
		},

		fecharPreviewMidia() {
			this.previewDialogOpen = false;
			this.previewMidia = { titulo: '', url: '' };
		},

		fecharDialog() {
			this.dialogOpen = false;
		},
	},
};
</script>

<style scoped>
.viagem-dialog {
	border-radius: 16px !important;
	border-color: #e6ebf1 !important;
}

.viagem-dialog__title {
	color: #1f2a44;
	font-weight: 800;
}

.viagem-new-btn {
	background-color: #10b981 !important;
	color: #ffffff !important;
	border-radius: 12px;
	font-weight: 700;
	letter-spacing: 0.01em;
	padding: 10px 16px;
	text-transform: none;
}

.viagem-confirmacao__texto {
	color: #1f2a44;
	font-size: 14px;
	line-height: 1.5;
}

.viagem-confirmacao__aviso {
	margin-top: 10px;
	color: #7a8ca6;
	font-size: 13px;
}

.despesa-dialog__subtitle {
	font-size: 12px;
	font-weight: 500;
	color: #7a8ca6;
	margin-top: 4px;
}

.despesa-resumo {
	display: flex;
	align-items: stretch;
	gap: 12px;
	flex-wrap: wrap;
}

.despesa-resumo__card {
	min-width: 180px;
	padding: 16px 18px;
	border-radius: 16px;
	border: 1px solid #e7edf5;
	background: #f8fbfa;
}

.despesa-resumo__label {
	color: #7a8ca6;
	font-size: 12px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin-bottom: 6px;
}

.despesa-resumo__value {
	color: #10233b;
	font-size: 22px;
	font-weight: 800;
	line-height: 1.1;
}

.despesa-form-card {
	border-radius: 18px !important;
	border-color: #dce8e4 !important;
	background: #ffffff;
	box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
}

.despesa-form-card__title {
	color: #10233b;
	font-size: 15px;
	font-weight: 800;
	padding: 16px 20px;
}

.despesa-tipo-badge {
	display: inline-flex;
	align-items: center;
	padding: 7px 10px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 700;
	white-space: nowrap;
}

.despesa-tipo-badge--fuel {
	background: rgba(16, 185, 129, 0.12);
	color: #0f9f6e;
}

.despesa-tipo-badge--route {
	background: rgba(37, 99, 235, 0.12);
	color: #2563eb;
}

.despesa-tipo-badge--support {
	background: rgba(217, 119, 6, 0.14);
	color: #b45309;
}

.despesa-tipo-badge--operation {
	background: rgba(8, 145, 178, 0.14);
	color: #0e7490;
}

.despesa-tipo-badge--vehicle {
	background: rgba(124, 58, 237, 0.12);
	color: #7c3aed;
}

.despesa-tipo-badge--alert {
	background: rgba(239, 68, 68, 0.12);
	color: #dc2626;
}

.despesa-tipo-badge--neutral {
	background: rgba(100, 116, 139, 0.14);
	color: #475569;
}

.despesa-detalhes {
	max-width: 360px;
	color: #54657d;
	font-size: 12px;
	line-height: 1.45;
}

.despesa-odometro {
	display: inline-flex;
	align-items: center;
	color: #1f2a44;
	font-size: 12px;
	font-weight: 700;
	white-space: nowrap;
}

.despesa-comprovantes {
	display: flex;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 8px;
	min-width: 96px;
}

.despesa-comprovante-acao {
	border-radius: 10px;
	border: 1px solid #d9e3ef;
	background: #ffffff !important;
	transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.despesa-comprovante-acao:hover {
	transform: translateY(-1px);
	box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
}

.despesa-comprovante-acao--odometro {
	color: #2563eb !important;
	border-color: rgba(37, 99, 235, 0.22);
	background: rgba(37, 99, 235, 0.05) !important;
}

.despesa-comprovante-acao--comprovante {
	color: #0f9f6e !important;
	border-color: rgba(16, 185, 129, 0.22);
	background: rgba(16, 185, 129, 0.05) !important;
}

.despesa-comprovantes__vazio {
	color: #7a8ca6;
	font-size: 12px;
}

.despesa-acoes {
	display: flex;
	justify-content: center;
	gap: 4px;
}

.despesa-preview-wrap {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 280px;
	background: #f8fbfa;
	border-radius: 16px;
	padding: 12px;
}

.despesa-preview-imagem {
	max-width: 100%;
	max-height: 70vh;
	object-fit: contain;
	border-radius: 12px;
	box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
}

.despesa-preview-vazio {
	padding: 24px;
	text-align: center;
	color: #7a8ca6;
}

.viagem-delete-btn {
	background-color: #ef4444 !important;
	color: #ffffff !important;
	border-radius: 12px;
	font-weight: 700;
	text-transform: none;
}
</style>